import { useEffect, useState, useMemo } from "react";
import { 
  getPackaging, 
  getPackagingAlerts,
  getPackagingStats,
  uploadPackaging, 
  downloadPackaging,
  updatePackagingItem,
  deletePackagingItem
} from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { 
  Package, 
  Upload, 
  Download, 
  Search,
  RefreshCw,
  AlertTriangle,
  Clock,
  MapPin,
  CheckCircle,
  XCircle,
  Trash2,
  Bell,
  TrendingDown,
  Globe,
  CloudDownload
} from "lucide-react";
import { toast } from "sonner";

export default function Packaging() {
  const [items, setItems] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourcingFilter, setSourcingFilter] = useState("all");
  const [editingQty, setEditingQty] = useState(null);
  const [activeTab, setActiveTab] = useState("inventory");
  const [syncingSheets, setSyncingSheets] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [itemsRes, alertsRes, statsRes] = await Promise.all([
        getPackaging(),
        getPackagingAlerts(),
        getPackagingStats()
      ]);
      setItems(itemsRes.data);
      setAlerts(alertsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load packaging data");
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = useMemo(() => {
    return items.filter(item => {
      // Search filter
      if (searchTerm && !item.item_name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      // Status filter
      if (statusFilter !== "all" && item.stock_status !== statusFilter) {
        return false;
      }
      // Sourcing filter
      if (sourcingFilter !== "all" && item.sourcing !== sourcingFilter) {
        return false;
      }
      return true;
    });
  }, [items, searchTerm, statusFilter, sourcingFilter]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await uploadPackaging(file);
      toast.success(response.data.message);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDownload = async () => {
    try {
      const response = await downloadPackaging();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'packaging.csv';
      a.click();
      toast.success("Packaging data exported");
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const handleSyncFromSheets = async () => {
    toast.error("Google Sheets sync has been permanently disabled.");
  };

  const handleQuantityChange = async (item, newQty) => {
    try {
      await updatePackagingItem(item.sr_no, { quantity: parseInt(newQty) });
      toast.success("Quantity updated");
      setEditingQty(null);
      await loadData();
    } catch (error) {
      toast.error("Update failed");
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.item_name}"?`)) return;
    try {
      await deletePackagingItem(item.sr_no);
      toast.success("Item deleted");
      await loadData();
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  const getStockBadge = (status) => {
    switch (status) {
      case 'in_stock':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" />In Stock</Badge>;
      case 'low_stock':
        return <Badge className="bg-amber-100 text-amber-700"><AlertTriangle className="w-3 h-3 mr-1" />Low Stock</Badge>;
      case 'out_of_stock':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Out of Stock</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const getUrgencyBadge = (urgency) => {
    switch (urgency) {
      case 'critical':
        return <Badge className="bg-red-600 text-white">Critical</Badge>;
      case 'high':
        return <Badge className="bg-orange-500 text-white">High</Badge>;
      case 'medium':
        return <Badge className="bg-yellow-500 text-white">Medium</Badge>;
      default:
        return null;
    }
  };

  const getSourcingBadge = (sourcing) => {
    if (sourcing === 'IN') {
      return <Badge variant="outline" className="border-green-500 text-green-700"><MapPin className="w-3 h-3 mr-1" />India (15d)</Badge>;
    }
    return <Badge variant="outline" className="border-blue-500 text-blue-700"><Globe className="w-3 h-3 mr-1" />China (60d)</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="packaging-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Packaging Inventory</h1>
          <p className="text-slate-500">Manage packaging materials and reorder alerts</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-1" />Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-slate-50 to-slate-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8 text-slate-600" />
              <div>
                <p className="text-2xl font-bold">{stats.total_items || 0}</p>
                <p className="text-xs text-slate-500">Total Items</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
              <div>
                <p className="text-2xl font-bold text-emerald-700">{stats.in_stock || 0}</p>
                <p className="text-xs text-emerald-600">In Stock</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-amber-600" />
              <div>
                <p className="text-2xl font-bold text-amber-700">{stats.low_stock || 0}</p>
                <p className="text-xs text-amber-600">Low Stock</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-red-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <XCircle className="w-8 h-8 text-red-600" />
              <div>
                <p className="text-2xl font-bold text-red-700">{stats.out_of_stock || 0}</p>
                <p className="text-xs text-red-600">Out of Stock</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <MapPin className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-green-700">{stats.india_sourced || 0}</p>
                <p className="text-xs text-green-600">India (15d)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Globe className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-blue-700">{stats.china_sourced || 0}</p>
                <p className="text-xs text-blue-600">China (60d)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts Banner */}
      {alerts.filter(a => a.urgency === 'critical' || a.urgency === 'high').length > 0 && (
        <Card className="bg-gradient-to-r from-red-500 to-orange-500 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell className="w-6 h-6 animate-pulse" />
                <div>
                  <p className="font-semibold">Reorder Alert!</p>
                  <p className="text-sm opacity-90">
                    {alerts.filter(a => a.urgency === 'critical').length} critical, {alerts.filter(a => a.urgency === 'high').length} high priority items need attention
                  </p>
                </div>
              </div>
              <Button 
                variant="secondary" 
                size="sm"
                onClick={() => setActiveTab('alerts')}
              >
                View Alerts
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="inventory">
            <Package className="w-4 h-4 mr-2" />Inventory ({items.length})
          </TabsTrigger>
          <TabsTrigger value="alerts">
            <Bell className="w-4 h-4 mr-2" />Reorder Alerts ({alerts.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inventory" className="mt-4">
          {/* Filters & Actions */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search items..."
                      className="pl-9 w-64"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      data-testid="packaging-search"
                    />
                  </div>
                  
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="in_stock">In Stock</SelectItem>
                      <SelectItem value="low_stock">Low Stock</SelectItem>
                      <SelectItem value="out_of_stock">Out of Stock</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={sourcingFilter} onValueChange={setSourcingFilter}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Sourcing" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sources</SelectItem>
                      <SelectItem value="IN">India (15d)</SelectItem>
                      <SelectItem value="CN">China (60d)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleSyncFromSheets} 
                    disabled={syncingSheets}
                    className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                  >
                    {syncingSheets ? <div className="spinner w-4 h-4 mr-1" /> : <CloudDownload className="w-4 h-4 mr-1" />}
                    Sync Sheets
                  </Button>
                  
                  <Button variant="outline" size="sm" onClick={handleDownload}>
                    <Download className="w-4 h-4 mr-1" />Export
                  </Button>
                  
                  <div className="relative">
                    <Input 
                      type="file" 
                      accept=".csv,.xlsx,.xls" 
                      className="absolute inset-0 opacity-0 cursor-pointer" 
                      onChange={handleUpload} 
                      disabled={uploading} 
                    />
                    <Button disabled={uploading} size="sm">
                      {uploading ? <div className="spinner w-4 h-4 mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                      Import
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Inventory Table */}
          <Card className="mt-4">
            <CardHeader className="py-3 px-4 border-b">
              <CardTitle className="text-sm font-medium">
                Packaging Items ({filteredItems.length})
              </CardTitle>
            </CardHeader>
            <ScrollArea className="h-[450px]">
              {filteredItems.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <Package className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <p className="text-lg">No packaging items found</p>
                  <p className="text-sm mt-2">Upload a file to add items</p>
                </div>
              ) : (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs font-semibold text-slate-500 uppercase w-16">Sr No</TableHead>
                      <TableHead className="text-xs font-semibold text-slate-500 uppercase">Item Name</TableHead>
                      <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center w-24">Qty</TableHead>
                      <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center">Sourcing</TableHead>
                      <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center">Lead Time</TableHead>
                      <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center">Status</TableHead>
                      <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center w-20">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredItems.map((item) => (
                      <TableRow 
                        key={item.sr_no}
                        className={item.stock_status === 'out_of_stock' ? 'bg-red-50' : item.stock_status === 'low_stock' ? 'bg-amber-50' : ''}
                      >
                        <TableCell className="font-mono text-sm">{item.sr_no}</TableCell>
                        <TableCell className="font-medium">
                          {item.item_name}
                          {item.category && (
                            <span className="ml-2 text-xs text-slate-400">[{item.category}]</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {editingQty === item.sr_no ? (
                            <Input
                              type="number"
                              defaultValue={item.quantity}
                              className="w-20 text-center"
                              autoFocus
                              onBlur={(e) => handleQuantityChange(item, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleQuantityChange(item, e.target.value);
                                if (e.key === 'Escape') setEditingQty(null);
                              }}
                            />
                          ) : (
                            <span 
                              className="cursor-pointer hover:bg-slate-100 px-2 py-1 rounded font-mono"
                              onClick={() => setEditingQty(item.sr_no)}
                              title="Click to edit"
                            >
                              {item.quantity}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {getSourcingBadge(item.sourcing)}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Clock className="w-3 h-3 text-slate-400" />
                            <span className="text-sm">{item.lead_time} days</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {getStockBadge(item.stock_status)}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDelete(item)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </ScrollArea>
          </Card>
        </TabsContent>

        <TabsContent value="alerts" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Reorder Alerts
              </CardTitle>
              <CardDescription>
                Items below {75} units threshold that need reordering. Lead times: India (15 days), China (60 days)
              </CardDescription>
            </CardHeader>
            <ScrollArea className="h-[500px]">
              {alerts.length === 0 ? (
                <CardContent>
                  <div className="p-12 text-center text-slate-500">
                    <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-300" />
                    <p className="text-lg">All items are well stocked!</p>
                    <p className="text-sm mt-2">No reorder alerts at this time</p>
                  </div>
                </CardContent>
              ) : (
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs font-semibold text-slate-500 uppercase">Priority</TableHead>
                        <TableHead className="text-xs font-semibold text-slate-500 uppercase">Item Name</TableHead>
                        <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center">Current Stock</TableHead>
                        <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center">Min Stock</TableHead>
                        <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center">Days Left</TableHead>
                        <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center">Lead Time</TableHead>
                        <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center">Source</TableHead>
                        <TableHead className="text-xs font-semibold text-slate-500 uppercase">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {alerts.map((alert, idx) => (
                        <TableRow 
                          key={idx}
                          className={
                            alert.urgency === 'critical' ? 'bg-red-50' : 
                            alert.urgency === 'high' ? 'bg-orange-50' : 
                            'bg-yellow-50'
                          }
                        >
                          <TableCell>{getUrgencyBadge(alert.urgency)}</TableCell>
                          <TableCell className="font-medium">{alert.item_name}</TableCell>
                          <TableCell className="text-center font-mono">
                            <span className={alert.current_stock === 0 ? 'text-red-600 font-bold' : 'text-amber-600'}>
                              {alert.current_stock}
                            </span>
                          </TableCell>
                          <TableCell className="text-center font-mono text-slate-500">{alert.min_stock}</TableCell>
                          <TableCell className="text-center">
                            <span className={alert.days_until_stockout < alert.lead_time ? 'text-red-600 font-semibold' : ''}>
                              ~{alert.days_until_stockout} days
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">
                              {alert.lead_time} days
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            {getSourcingBadge(alert.sourcing)}
                          </TableCell>
                          <TableCell>
                            <Badge 
                              className={
                                alert.urgency === 'critical' ? 'bg-red-600' : 
                                alert.urgency === 'high' ? 'bg-orange-500' : 
                                'bg-yellow-500'
                              }
                            >
                              {alert.action}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              )}
            </ScrollArea>
          </Card>

          {/* Lead Time Reference */}
          <Card className="mt-4">
            <CardContent className="p-4">
              <div className="flex items-center gap-8">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-green-600" />
                  <span className="text-sm"><strong>India (IN):</strong> 15 days lead time</span>
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-blue-600" />
                  <span className="text-sm"><strong>China (CN):</strong> 60 days lead time</span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-amber-600" />
                  <span className="text-sm"><strong>Low Stock:</strong> Below 75 units</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
